import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { A } from '@tink/core/src/components/atoms/A/A';
import { Button } from '@tink/core/src/components/atoms/Button/Button';
import { Spacer } from '@tink/core/src/components/atoms/Spacer/Spacer';
import { List } from '@tink/core/src/components/atoms/List/List';
import { connect, ConnectedProps } from 'react-redux';
import { Severity } from '@tink/core/src/components/atoms/Icons/AlertIcon';
import { ContentOverlay } from '../../../../../../../components/molecules/ContentOverlay/ContentOverlay';
import { ContentBody } from '../../../../../../../components/atoms/ContentBody/ContentBody';
import { Header } from '../../../../../../../components/organisms/Header/Header';
import { Footer } from '../../../../../../../components/atoms/Footer/Footer';
import { LineItem } from '../../../../../../../components/atoms/LineItem/LineItem';
import { H1 } from '../../../../../../../components/atoms/Heading/Heading';
import { Caption } from '../../../../../../../components/atoms/Text/Text';
import { PageView, UserInteractionEvent } from '../../../../../../../models/analytics';
import {
  InteractionElement,
  parentMessenger,
} from '../../../../../../../core/Analytics/ParentApplicationEventMessenger';
import { setApplicationError as setApplicationErrorConnect } from '../../../../../../../stores/app-state-store';
import { ERR_USER_CANCELLED } from '../../../../../../../utilities/error';
import { useAnalytics } from '../../../../../../../core/Analytics/AnalyticsProvider';
import { commonMessages } from '../../../../../../../components/atoms/CommonMessages/messages';
import { VerboseErrorScreen } from '../../../../../../../components/screens/VerboseErrorScreen/VerboseErrorScreen';
import { messages } from './messages';

export type ExtendBankIdProps = PropsFromRedux;

export const ExtendBankIdPresenter: React.FC<ExtendBankIdProps> = ({ setApplicationError }) => {
  const { tinklinkAnalytics } = useAnalytics();
  const [showInstructions, setShowInstructions] = useState(false);

  const swedbankMessage = 'Swedbank';
  const swedbankLink = (
    <A
      color="primary"
      href="https://www.swedbank.se/share/layer-content/privat/digitala-tjanster/mobilt-bankid/mobilt-bankid-med-utokad-anvandning/aktivera-mobilt-bankid-med-utokad-anvandning.html"
    >
      {swedbankMessage}
    </A>
  );

  const sparbankenMessage = 'Sparbanken';
  const sparbankenLink = (
    <A
      color="primary"
      href="https://www.sparbankenskane.se/privat/digitala-tjanster/mobilt-bankid.html"
    >
      {sparbankenMessage}
    </A>
  );

  const DetailedErrorLink = (
    <a
      target="_blank"
      rel="noopener noreferrer"
      href="https://online.swedbank.se/app/privat/betala-overfora"
    >
      <FormattedMessage {...messages.here} />
    </a>
  );

  const onBack = () => {
    tinklinkAnalytics.sendInteractionEvent({
      action: `${PageView.EXTEND_BANKID_SCREEN}/${UserInteractionEvent.CLOSE_BUTTON}`,
      view: PageView.EXTEND_BANKID_SCREEN,
    });

    parentMessenger.fireInteractionEvent({
      pageView: PageView.EXTEND_BANKID_SCREEN,
      element: InteractionElement.CLOSE_BUTTON,
    });

    setApplicationError({
      ...ERR_USER_CANCELLED,
    });
  };

  if (!showInstructions) {
    return (
      <VerboseErrorScreen
        viewEvent={PageView.EXTEND_BANKID_SCREEN}
        severity={Severity.WARNING}
        title={<FormattedMessage {...messages.swedbankTitle} />}
        body={[
          { text: <FormattedMessage {...messages.swedbank} values={{ DetailedErrorLink }} /> },
          {
            text: (
              <button type="button" onClick={() => setShowInstructions(true)}>
                <FormattedMessage {...messages.buttonText} />
              </button>
            ),
          },
        ]}
        primaryButtonText={<FormattedMessage {...commonMessages.ok} />}
        primaryAction={onBack}
      />
    );
  }

  return (
    <ContentOverlay>
      <Header onBack={undefined} hideCloseButton pageView={PageView.EXTEND_BANKID_SCREEN} />
      <ContentBody>
        <H1>
          <FormattedMessage {...messages.extendBankIdtitle} />
        </H1>
        <List>
          <LineItem index={1}>
            <FormattedMessage {...messages.extendBankIdStepOne} />
          </LineItem>
          <LineItem index={2}>
            <FormattedMessage {...messages.extendBankIdStepTwo} />
          </LineItem>
          <LineItem index={3}>
            <FormattedMessage {...messages.extendBankIdStepThree} />
          </LineItem>
          <LineItem index={4}>
            <FormattedMessage {...messages.extendBankIdStepFour} />
          </LineItem>
          <LineItem index={5}>
            <FormattedMessage {...messages.extendBankIdStepFive} />
          </LineItem>
          <LineItem index={6}>
            <FormattedMessage {...messages.extendBankIdStepSix} />
          </LineItem>
          <LineItem index={7}>
            <FormattedMessage {...messages.extendBankIdStepSeven} />
          </LineItem>
        </List>
        <Spacer size="normal" />
        <Caption>
          <FormattedMessage {...messages.helpText} values={{ swedbankLink, sparbankenLink }} />
        </Caption>
      </ContentBody>
      <Footer>
        <Button
          size="large"
          color="primary"
          variant="contained"
          onClick={() => setShowInstructions(false)}
        >
          <FormattedMessage {...messages.extendBankIdbuttonText} />
        </Button>
      </Footer>
    </ContentOverlay>
  );
};

const mapDispatchToProps = {
  setApplicationError: setApplicationErrorConnect,
};

const connector = connect(undefined, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const ExtendBankId = connector(ExtendBankIdPresenter);
